/* Sondos product-listing pages — one system for "the products are always
 * there": predictive auto-load on scroll, Back/reload restore, and the mobile
 * sort label. No library, nothing runs on first tap.
 *
 * Loaded by BOTH sections/main-collection-product-grid.liquid and
 * sections/main-search.liquid (owner, 2026-09-12 — search had been left on
 * Dawn's numbered pagination). Nothing here is collection-specific: the whole
 * contract is `#ProductGridContainer`, `#product-grid`, `[data-scg-more]`
 * (whose data-next-url + data-section-id give the Section Rendering URL), and
 * a session key derived from path + facets + sort. Keep it that way — a
 * `/collections/` path test would silently break search.
 *
 * 1. Auto-load (owner, 2026-09-12 — replaced the «عرض المزيد» button; rebuilt
 *    as a page store the same day). The Liquid renders a sentinel
 *    (`[data-scg-more]`) after the grid whenever a next page exists, carrying
 *    the next page's URL and the section id. Pages are fetched through the
 *    Section Rendering API (`?section_id=`, the mechanism facets.js already
 *    uses) into a store — memory first, then sessionStorage, then the
 *    network, one in-flight request per page — and inserted into
 *    #product-grid in chunks of 8 across animation frames, because one 24-card
 *    append cost a 300–460 ms task on a CPU×4 phone and that lands mid-scroll.
 *
 *    What makes the cards *already there* when she arrives:
 *      - insertion three viewports early (IntersectionObserver rootMargin),
 *        and earlier still when she is moving fast: a passive scroll listener
 *        keeps a velocity estimate and inserts as soon as the sentinel is
 *        within LEAD_MS of travel;
 *      - the page after the one just inserted is fetched at once, so the
 *        insertion on approach is network-free — the fetch (1.2–1.3 s on
 *        Slow 4G) was the whole wait;
 *      - at FAST_PX_S the page after that is fetched too, and the lazy images
 *        of the cards in the next two viewports are switched to eager, so
 *        the browser's own lazy threshold (≈1250 px) is not the limit.
 *    Measured before this (R3, 390 px): at 7000 px/s on a fast network the
 *    spinner showed for ~190 ms and images trailed by 620 ms; on Slow 4G the
 *    same from 4500 px/s, up to 630 ms of blank and 1.1 s of empty images.
 *
 *    The address bar is NOT moved to `?page=N` as pages load (owner bug
 *    report, 2026-09-12). It was, and it stranded her: at the end of a
 *    50-product collection the URL read `?page=3`; a tap into a product and
 *    back — or a refresh — reloaded THAT url, a page of two products with
 *    nothing to auto-load and the numbered strip showing. With the URL left
 *    alone, back/refresh land on the collection itself, and section 3 puts
 *    her back. `?page=N` links still exist for crawlers and no-JS in the
 *    hidden strip.
 *
 *    Failure never navigates (she is scrolling, a jump would be hostile):
 *    the sentinel is removed and the numbered pagination comes back, which
 *    is also what she gets with scripting off, without IntersectionObserver,
 *    and for crawlers. Save-Data on → no prefetch, nothing loads until the
 *    sentinel is actually on screen.
 *
 *    Custom elements inside inserted cards (product-form, modal-opener,
 *    quick-add-modal) upgrade on insertion — a <template>'s content has no
 *    registry, so they are inert there and become live the moment they join
 *    this document. Same path facets.js relies on with innerHTML.
 *
 * 2. The mobile sort control's value label. facets.js re-renders the sheet
 *    after an in-page sort change but not the control row, so the label is
 *    refreshed here from the option she just picked.
 *
 * 3. Back restore (owner, 2026-09-12 — Back only; a refresh deliberately
 *    starts at the top, see `isReturn`). bfcache alone is not enough:
 *    when the browser declines it (in-app browsers, memory pressure), Back
 *    re-fetches page 1 and the offset she was at clamps to the bottom of it.
 *    Every fetched page is already in sessionStorage (section 1), so on
 *    `pagehide` only the small things are written: her offset, the grid's
 *    top and height, the last page inserted, and the cards on her screen
 *    with their boxes. The head script in layout/theme.liquid then paints
 *    those on-screen cards at the same coordinates the moment <body> opens —
 *    before the page has arrived — and puts the pages back into the real
 *    grid while it is still being parsed. This file finishes below the fold
 *    from the same store, waits for the real images, lifts the ghost, and
 *    leaves the auto-load pointed at the right next page with that page
 *    already fetched, so she can keep scrolling at once. Nothing is fetched
 *    for the restore unless a page had fallen out of the session budget.
 *
 *    The grid on a cold deep load sits 56 px higher than the one she left:
 *    the smart header compensates its folded search row with a wrapper
 *    margin only after a pin *transition*, never when a page loads already
 *    pinned (its `flowH0` is measured at rest, at the top). That is a
 *    property of every deep load, not of this restore, and the header is
 *    not touched here. Instead a spacer before the grid holds the grid's
 *    document top exactly where she left it, and a ResizeObserver on the
 *    header wrapper lets that spacer shrink by whatever the header later
 *    adds back — so nothing under her ever moves, in either direction.
 *
 * Everything is delegated on `document` or re-armed from a MutationObserver
 * on #ProductGridContainer: facets.js replaces its innerHTML on every filter
 * change, and nothing here has to be re-bound by hand when it does.
 */
(function () {
  'use strict';

  var ACTIVE = 'scg--more-active';
  var CHUNK = 8;
  /* Three viewports of lead for the observer. Measured on Slow 4G / CPU×4:
     fetch 1.2–1.3 s end to end + ~0.3 s of chunked append. The velocity
     trigger below takes over above ~1500 px/s. */
  var LEAD = '300% 0px';
  /* Travel time the next page must be inserted ahead of. At 4500 px/s that
     is 7200 px ≈ 8.5 viewports; the page is already in the store, so this
     buys insertion + image time, not network time. */
  var LEAD_MS = 1600;
  /* Above this the page after next is fetched too and upcoming images go
     eager. 2500 px/s is a real flick, not a browse. */
  var FAST_PX_S = 2500;
  var TTL = 30 * 60 * 1000;
  /* Session budget for stored pages, in chars (UTF-16 doubles it in memory;
     Safari's quota is ~5 MB). One page of 24 cards is ~140k chars. */
  var PG_BUDGET = 2400000;
  var PKEY = 'sondos:scg:pos';
  var GKEY = 'sondos:scg:pg';
  var NOISE = ['pb', 'preview_theme_id', '_fd', '_ab', '_sm_byp', 'page'];

  var hasIO = 'IntersectionObserver' in window;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var root = document.documentElement;

  function container() { return document.getElementById('ProductGridContainer'); }
  function grid() { return document.getElementById('product-grid'); }
  function sentinel() { return document.querySelector('[data-scg-more]'); }

  /* Identity of *this* result set: path + facets + sort, minus preview and
     pagination noise, so a filtered view never restores a different one.
     Read at use time — facets.js rewrites the URL on every change. */
  function stateKey() {
    try {
      var u = new URL(location.href);
      NOISE.forEach(function (p) { u.searchParams.delete(p); });
      u.searchParams.sort();
      return u.pathname + u.search;
    } catch (e) {
      return location.pathname;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Numbered pagination is hidden from the moment auto-load can work, and
     stays hidden — it is not re-derived from "is there a sentinel?".

     Both the natural end and a failed fetch remove the sentinel, but they
     need opposite endings (owner, 2026-09-12): after the last page there is
     nothing left to navigate to, and the strip in the document is still the
     page-1 one, so it would surface «… 3 2 1» with 1 marked current under
     300+ cards she has already scrolled past — wrong and alarming. After a
     failure it is her only way forward, so it comes back.

     `latched` is set once the page shows it can auto-load; `fallback` is the
     one thing that un-sets it. A facets re-render clears both: new results,
     new attempt. */
  var latched = false;
  var fallback = false;

  function syncPagination() {
    var c = container();
    if (!c) return;
    if (hasIO && !fallback && sentinel()) latched = true;
    c.classList.toggle(ACTIVE, latched && !fallback);
  }

  /* --- polite announcement for screen readers, one persistent region --- */
  var live = null;
  function announce(text) {
    if (!text) return;
    if (!live) {
      live = document.createElement('p');
      live.className = 'scg-more__status';
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = text;
  }

  /* ------------------------------------------------------------------ */
  /* The page store. A page is `{ lis, more }`: the <li> cards as one HTML
     string, and the fragment's own sentinel (or '' at the end of the
     collection). Memory → session → network, one promise per page in
     flight. Pages are written to the session the moment they arrive, so a
     Back has them without anything being serialised on the way out. */
  var mem = {};
  var inflight = {};
  var pageBase = null;   /* the sentinel's next-url pattern, `page=` included */
  var sectionId = null;

  function learnBase(box) {
    var next = box && box.getAttribute('data-next-url');
    var sid = box && box.getAttribute('data-section-id');
    if (next && sid && /[?&]page=\d+/.test(next)) { pageBase = next; sectionId = sid; }
  }

  function pageUrl(n) {
    if (!pageBase || !sectionId) return null;
    var u = pageBase.replace(/([?&]page=)\d+/, '$1' + n);
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
  }

  function parsePage(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var lis = doc.querySelectorAll('#product-grid > li');
    if (!lis.length) return null;
    var out = '';
    for (var i = 0; i < lis.length; i++) out += lis[i].outerHTML;
    var more = doc.querySelector('[data-scg-more]');
    return { lis: out, more: more ? more.outerHTML : '' };
  }

  function sessionGet(n) {
    try {
      var raw = sessionStorage.getItem(GKEY + '|' + stateKey() + '|' + n);
      if (!raw) return null;
      var rec = JSON.parse(raw);
      if (!rec || !rec.lis || !(rec.t > 0) || Date.now() - rec.t > TTL) return null;
      return rec;
    } catch (e) { return null; }
  }

  /* Oldest pages go first when the budget is exceeded; every record carries
     its own time so the prune needs nothing else. */
  function sessionPut(n, page, resultKey) {
    var k = GKEY + '|' + resultKey + '|' + n;
    var rec = JSON.stringify({ t: Date.now(), lis: page.lis, more: page.more });
    try {
      var names = [], total = rec.length;
      for (var i = 0; i < sessionStorage.length; i++) {
        var name = sessionStorage.key(i);
        if (name && name.indexOf(GKEY + '|') === 0 && name !== k) {
          var v = sessionStorage.getItem(name) || '';
          /* `t` is the record's first key (see `rec` above), so it is read
             off the head of the string. JSON.parse of every stored page —
             ~140k chars each — cost 24ms per store at 13 pages on a 4× CPU
             phone, and this runs once per page fetched (QA, 2026-10-22). */
          var m = /^\{"t":(\d+)/.exec(v);
          var t = m ? +m[1] : 0;
          names.push({ name: name, t: t, size: v.length });
          total += v.length;
        }
      }
      names.sort(function (a, b) { return a.t - b.t; });
      while (total > PG_BUDGET && names.length) { var old = names.shift(); sessionStorage.removeItem(old.name); total -= old.size; }
      sessionStorage.setItem(k, rec);
    } catch (e) { /* quota / private mode: memory still has it */ }
  }

  function loadPage(n) {
    var resultKey = stateKey();
    var k = resultKey + '|' + n;
    if (mem[k]) return Promise.resolve(mem[k]);
    if (inflight[k]) return inflight[k];
    var fromSession = sessionGet(n);
    if (fromSession) { mem[k] = { lis: fromSession.lis, more: fromSession.more }; return Promise.resolve(mem[k]); }
    var url = pageUrl(n);
    if (!url) return Promise.reject(new Error('no base'));
    var p = fetch(url).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (html) {
      var page = parsePage(html);
      if (!page) throw new Error('no items');
      mem[k] = page;
      sessionPut(n, page, resultKey);
      return page;
    });
    inflight[k] = p;
    p.then(function () {
      delete inflight[k];
      delete dead[k];
    }, function () {
      delete inflight[k];
      var d = dead[k];
      dead[k] = { n: (d ? d.n : 0) + 1, t: Date.now() };
    });
    return p;
  }

  /* Speculation backs off; demand never does (independent audit, 2026-09-13).
     `onScrollFrame` calls prefetch once per animation frame while she is
     moving fast. Nothing recorded that a page had already failed — the
     in-flight promise is deleted on rejection and no result is stored — so a
     page that could not load was re-requested every frame. Measured with an
     injected connection failure: 226 requests for two dead pages, median
     17 ms apart. On a flaky phone connection that is battery, data, and a
     real chance of being rate-limited, which then breaks filtering too.

     The back-off is deliberately confined to PREFETCH. `insertNext` calls
     loadPage directly and is never suppressed: at that moment she has
     actually arrived at the sentinel, the network may well have recovered,
     and one honest attempt is what stands between her and the numbered
     pagination. A single failure there already ends the auto-load through
     `fail()`, so it cannot storm. */
  var dead = {};
  var DEAD_COOL_MS = 10000;   /* a dropped request on a phone is normal: wait, then allow one more */
  var DEAD_MAX = 1;           /* ...and after that second failure, stop speculating on this page */

  function prefetch(n) {
    if (saveData || !n || !pageBase) return;
    var d = dead[stateKey() + '|' + n];
    if (d && (d.n > DEAD_MAX || Date.now() - d.t < DEAD_COOL_MS)) return;
    loadPage(n).catch(function () {});
  }

  /* ------------------------------------------------------------------ */
  /* Insertion. `nextPage` is the page the sentinel stands for; `inserting`
     guards against the observer and the velocity trigger both firing for
     the same page. */
  var nextPage = null;
  var inserting = false;
  var restoringPages = false;
  /* Same hold as the header: cart-notification pins body and drops scrollY
     to 0, then close restores it. That pair looks like a flick and used to
     insert the next page / eager-load images under the sheet. */
  var lockHold = false;
  var stopRestoreLayout = null;
  var sentinelTop = Infinity;   /* document top of the sentinel, refreshed after every change */

  function nodesOf(html) {
    var t = document.createElement('template');
    t.innerHTML = html;
    return Array.prototype.slice.call(t.content.children);
  }

  function appendChunked(g, items, done) {
    var i = 0;
    (function step() {
      if (grid() !== g) return;
      var frag = document.createDocumentFragment();
      for (var n = 0; n < CHUNK && i < items.length; n++, i++) frag.appendChild(items[i]);
      g.appendChild(frag);
      if (i < items.length) requestAnimationFrame(step);
      else done();
    })();
  }

  function measureSentinel() {
    var box = sentinel();
    sentinelTop = box ? box.getBoundingClientRect().top + window.pageYOffset : Infinity;
  }

  /* Swap the sentinel for the one the fragment carried, or drop it at the
     end of the collection — the strip stays hidden either way (this is the
     success path, `fail` is the other). */
  function placeSentinel(oldBox, moreHtml) {
    if (moreHtml) {
      var fresh = nodesOf(moreHtml)[0];
      if (fresh) { oldBox.replaceWith(fresh); learnBase(fresh); }
      else oldBox.remove();
    } else {
      oldBox.remove();
    }
    syncPagination();
    measureSentinel();
  }

  function insertNext() {
    /* The cheap flags first: onScrollFrame calls this every frame of a fast
       scroll, and `sentinel()` is an attribute query over the whole document
       (~6ms at 4× CPU once 300 cards are in). */
    if (lockHold || restoringPages || inserting || !nextPage || scrollLocked()) return;
    var box = sentinel();
    var g = grid();
    if (!box || !g) return;
    inserting = true;
    box.setAttribute('data-scg-busy', '');
    var n = nextPage;
    var status = box.getAttribute('data-status');
    loadPage(n).then(function (page) {
      if (grid() !== g || sentinel() !== box) return; /* a newer grid owns its own flags */
      appendChunked(g, nodesOf(page.lis), function () {
        if (grid() !== g || sentinel() !== box) return;
        placeSentinel(box, page.more);
        /* track the deepest page actually in the grid — remember() saves this
           as `last`, and the restore rebuilds exactly this many pages. A
           prefetched-but-not-inserted page does not count. */
        if (n > lastInserted) lastInserted = n;
        nextPage = page.more ? n + 1 : null;
        inserting = false;
        announce(status);
        arm();
        prefetch(nextPage);
        if (fast) { prefetch(nextPage && nextPage + 1); warmImages(); }
      });
    }).catch(function () {
      if (grid() !== g || sentinel() !== box) return;
      inserting = false;
      fail(box);
    });
  }

  function fail(box) {
    if (box && box.parentNode) box.remove();
    fallback = true;
    nextPage = null;
    syncPagination();
    measureSentinel();
  }

  /* --- approach observer: fires once per sentinel, three viewports early --- */
  var io = null;
  function arm() {
    if (!hasIO) return;
    var box = sentinel();
    if (!box || box.hasAttribute('data-scg-armed')) return;
    box.setAttribute('data-scg-armed', '');
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          insertNext();
        });
      }, { rootMargin: saveData ? '0px' : LEAD });
    }
    io.observe(box);
  }

  /* --- velocity: one passive listener, one rAF per burst of scroll events.
     Speed is an exponential average of |dy|/dt so a single jittery frame
     does not flip `fast`. The sentinel's top is cached, so the per-frame
     work is arithmetic, not layout. */
  var lastY = window.pageYOffset || 0;
  var lastT = performance.now();
  var speed = 0;
  var fast = false;
  var scrollTick = false;
  var lastWarm = 0;

  function onScrollFrame() {
    scrollTick = false;
    if (lockHold || scrollLocked()) return;
    var y = window.pageYOffset || 0;
    var t = performance.now();
    var dt = t - lastT;
    if (dt > 0) {
      var v = Math.abs(y - lastY) / dt * 1000;
      speed = speed * 0.6 + v * 0.4;
    }
    var down = y > lastY;
    lastY = y; lastT = t;
    fast = speed > FAST_PX_S;
    if (restoringPages || !down || saveData) return;
    var ahead = sentinelTop - (y + window.innerHeight);
    if (ahead < speed * LEAD_MS / 1000) insertNext();
    if (fast) {
      prefetch(nextPage && nextPage + 1);
      if (t - lastWarm > 250) { lastWarm = t; warmImages(); }
    }
  }
  window.addEventListener('scroll', function () {
    if (!scrollTick) { scrollTick = true; requestAnimationFrame(onScrollFrame); }
  }, { passive: true });
  function releaseLockHold() {
    lastY = window.pageYOffset || 0;
    lastT = performance.now();
    speed = 0;
    fast = false;
    lockHold = false;
    measureSentinel();
  }
  document.addEventListener('cart:notification-open', function () { lockHold = true; });
  document.addEventListener('cart:notification-close', function () {
    lockHold = true;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { releaseLockHold(); });
    });
  });

  /* Cards within two viewports below the fold: their lazy images go eager
     once. Chrome's lazy threshold is ~1250 px on a good connection — at
     4500 px/s that is a quarter of a second. */
  /* Walked forward from the first card not yet decided, never from the end:
     the backward walk measured every card below the three-viewport limit on
     every call — 1,049 getBoundingClientRect calls in 40 scroll frames, the
     single largest JS cost of the auto-load (942ms at 4× CPU, QA 2026-10-22).
     Cards are in document order, so the first one past the limit ends the
     walk and the index resumes there next time. Same cards end up eager. */
  var warmIdx = 0;
  var warmGrid = null;
  function warmImages() {
    var g = grid();
    if (!g) return;
    if (g !== warmGrid) { warmGrid = g; warmIdx = 0; }
    var limit = window.innerHeight * 3;
    var cards = g.children;
    for (; warmIdx < cards.length; warmIdx++) {
      var li = cards[warmIdx];
      var top = li.getBoundingClientRect().top;
      if (top > limit) return;
      if (top < -200 || li.hasAttribute('data-scg-warm')) continue;
      li.setAttribute('data-scg-warm', '');
      var imgs = li.querySelectorAll('img[loading="lazy"]');
      for (var m = 0; m < imgs.length; m++) imgs[m].loading = 'eager';
    }
  }

  /* ------------------------------------------------------------------ */
  /* 3. Back / reload restore. */
  var lastInserted = 1;   /* highest page number present in the grid */
  var departure = null;  /* belongs to this document, including its bfcache lifetime */

  function storePosition(key, position, ghost) {
    var compact = JSON.stringify(position);
    try {
      try {
        sessionStorage.setItem(key, compact);
      } catch (e) {
        /* Page HTML is expendable (restore can fetch it again); the offset
           is not. Only evict our page cache, never another feature's data. */
        var pages = [];
        for (var i = 0; i < sessionStorage.length; i++) {
          var name = sessionStorage.key(i);
          if (name && name.indexOf(GKEY + '|') === 0) pages.push(name);
        }
        var stored = false;
        for (var j = 0; j < pages.length && !stored; j++) {
          sessionStorage.removeItem(pages[j]);
          try { sessionStorage.setItem(key, compact); stored = true; } catch (x) {}
        }
        if (!stored) return;
      }
      /* A failed larger write leaves the compact record intact. Ghost HTML
         must never decide whether Back can remember the customer's place. */
      sessionStorage.setItem(key, JSON.stringify(Object.assign({}, position, { ghost: ghost })));
    } catch (e) { /* blocked storage: bfcache still has the document snapshot */ }
  }

  function remember() {
    var g = grid();
    if (!g || !g.children.length || scrollLocked()) return;
    var gr = g.getBoundingClientRect();
    var gt = Math.round((gr.top + window.pageYOffset) * 10) / 10;
    var vh = window.innerHeight;
    var ghost = [];
    for (var j = 0; j < g.children.length; j++) {
      var li = g.children[j];
      var lr = li.getBoundingClientRect();
      if (lr.bottom <= 0 || lr.top >= vh) continue;
      var card = li.firstElementChild || li;
      var r = card.getBoundingClientRect();
      var a = card.querySelector('a[href]');
      ghost.push({
        dt: Math.round((r.top + window.pageYOffset - gt) * 10) / 10,   /* relative to the grid's top */
        l: Math.round(r.left * 10) / 10,
        w: Math.round(r.width * 10) / 10,
        h: Math.round(r.height * 10) / 10,
        href: a ? a.getAttribute('href') : '',
        html: card.outerHTML
      });
    }
    /* The smart header's state travels with the offset. Without it the
       header initialises shown on the way back (its init calls show()), so a
       page she left with the header hidden returns with it on screen and
       then slides away on her first scroll — a flash produced purely by the
       restore. sections/sondos-header.liquid reads the marker this becomes. */
    var skh = document.querySelector('.skh');
    var position = {
        y: Math.round(window.pageYOffset || 0),
        gt: gt,
        h: Math.round(gr.height),
        last: lastInserted,
        end: !sentinel() && !fallback,
        hdr: skh && skh.classList.contains('skh--hidden') ? 'hidden' : 'shown',
        t: Date.now()
    };
    departure = { key: stateKey(), grid: g, y: position.y };
    storePosition(PKEY + '|' + departure.key, position, ghost);
  }
  /* pagehide, not beforeunload: it fires on the way into bfcache too, and
     unlike beforeunload it never disqualifies the page from it. */
  window.addEventListener('pagehide', remember);
  window.addEventListener('pagehide', function () {
    /* Frozen rAF callbacks must not resume an older correction on the next
       bfcache return and compete with that return's departure snapshot. */
    if (stopRestoreLayout) stopRestoreLayout();
    removeEl(byClass('scg-ghost'));
  });

  /* A bfcache return executes neither the head script nor init(). In
     particular, leaving during a manual restore/reload can freeze an entry
     with native restoration disabled. Reuse its existing grid and offset;
     rebuilding here would duplicate every auto-loaded page. */
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || !departure || departure.key !== stateKey() || departure.grid !== grid() || scrollLocked()) return;
    assertedY = -1;
    holdOffset(departure.y);
    try { history.scrollRestoration = 'auto'; } catch (e) {}
    lastY = window.pageYOffset || 0;
    lastT = performance.now();
    speed = 0;
    fast = false;
    measureSentinel();
  });

  function saved() {
    var raw;
    try { raw = sessionStorage.getItem(PKEY + '|' + stateKey()); } catch (e) { return null; }
    if (!raw) return null;
    var s;
    try { s = JSON.parse(raw); } catch (e) { return null; }
    if (!s || !(s.y > 0) || !(s.h > 0) || !(s.last >= 1)) return null;
    if (!(s.t > 0) || Date.now() - s.t > TTL) return null;
    return s;
  }

  /* Only a Back/Forward restores (owner, 2026-09-12). A refresh is a request
     for the page as it ships — she gets the top, like any fresh visit. A
     fresh visit from the menu must land at the top too; yanking her down
     there would be the bug. theme.liquid's head script holds the other half
     of this rule: on a reload it takes scroll restoration away from the
     browser so its own clamped guess cannot yank her down after first paint. */
  function isReturn() {
    try {
      var e = performance.getEntriesByType('navigation')[0];
      if (e && e.type) return e.type === 'back_forward';
    } catch (x) {}
    return (performance.navigation && performance.navigation.type) === 2;
  }

  /* The cart notification / drawer lock scrolling with
     `body { position: fixed; top: -Ypx }`. While that is on, every document
     coordinate on the page is displaced by the offset, so nothing here may
     measure or move the page: one fit() during a lock sized the pad to 7592px
     and left a blank sheet behind the notification (owner's screenshot,
     2026-09-12). */
  function scrollLocked() {
    if (root.classList.contains('sds-scroll-locked')) return true;
    try { return getComputedStyle(document.body).position === 'fixed'; } catch (e) { return false; }
  }

  function byClass(cls) { return document.querySelector('.' + cls); }
  function removeEl(el) { if (el && el.parentNode) el.parentNode.removeChild(el); }

  /* Everything the head script may have left, gone in one go — used on every
     path that decides not to (or cannot) finish a restore. */
  function clearRestoreArtifacts() {
    if (stopRestoreLayout) stopRestoreLayout();
    removeEl(byClass('scg-ghost'));
    root.removeAttribute('data-scg-restoring');
    root.removeAttribute('data-scg-injected');
    root.removeAttribute('data-scg-header');
    root.style.minHeight = '';
    root.style.overflowAnchor = '';
  }

  /* The flow guard: the grid's document top stays at `gt` for as long as
     this page lives, whatever changes height above it. It is the grid's own
     margin-top, not a spacer, because it has to work in BOTH directions: the
     smart header applies its folded-search compensation as a wrapper
     margin-bottom only after a pin transition, so a restored page starts
     56 px short above the grid and later gains it back — a spacer could push
     the grid down but never pull it up, and that one-way limit is what let
     the grid slide ~56 px out from under the ghost (owner's video). A margin
     change resizes nothing, so a ResizeObserver never sees it: the settle
     loop re-fits every frame instead, and the observer covers the rest. */
  function guardFlow(g, gt) {
    var stopped = false;
    var observer = null;
    var baseAttr = g.getAttribute('data-scg-base-mt');
    var base = baseAttr != null ? parseFloat(baseAttr) : (parseFloat(getComputedStyle(g).marginTop) || 0);
    var fit = function () {
      if (stopped || scrollLocked() || grid() !== g) return;
      var cur = parseFloat(g.style.marginTop);
      if (isNaN(cur)) cur = base;
      var top = g.getBoundingClientRect().top + window.pageYOffset;
      var want = cur + (gt - top);
      if (Math.abs(want - cur) < 0.5) return;
      g.style.marginTop = (Math.round(want * 10) / 10) + 'px';
    };
    fit();
    var hdr = document.getElementById('shopify-section-sondos-header');
    if (hdr && 'ResizeObserver' in window) {
      observer = new ResizeObserver(function () { fit(); });
      observer.observe(hdr);
    }
    fit.stop = function () {
      stopped = true;
      if (observer) observer.disconnect();
    };
    return fit;
  }

  /* The ghost the head script painted is lifted only once what is under it
     is identical: the real cards are in place and their images have arrived
     (same URLs, so from cache, but decode is not free). The real grid is
     already on the ghost's pixels thanks to the flow guard; if anything is
     still off and she has not moved, the grid is nudged onto the ghost —
     never the other way round — in the same frame the ghost goes. Capped so
     a stuck image cannot keep the ghost forever. */
  function liftGhost(g, s) {
    var ghost = byClass('scg-ghost');
    if (!ghost) return;
    var vh = window.innerHeight;
    var pendingImgs = [];
    var all = g.querySelectorAll('img');
    for (var i = 0; i < all.length; i++) {
      var r = all[i].getBoundingClientRect();
      if (r.bottom > 0 && r.top < vh && !all[i].complete) pendingImgs.push(all[i]);
    }
    var done = false;
    function lift() {
      if (done) return;
      done = true;
      requestAnimationFrame(function () {
        removeEl(ghost);
      });
    }
    if (!pendingImgs.length) return lift();
    var left = pendingImgs.length;
    function one() { if (--left <= 0) lift(); }
    pendingImgs.forEach(function (img) {
      img.addEventListener('load', one, { once: true });
      img.addEventListener('error', one, { once: true });
    });
    setTimeout(lift, 1500);
  }

  /* Hold the offset while the grid is rebuilt. The browser's own restoration
     cannot be relied on: `scrollRestoration` lives on the history entry, and
     one refresh of this collection leaves it 'manual' for every later Back to
     the same entry. So the offset is asserted here — but only while she has
     not taken over. `assertedY` is the last value we set; if the page has
     moved away from it by more than a thumb's worth, she is scrolling and
     nothing here touches the offset again. */
  var assertedY = -1;
  function takenOver() {
    var cur = window.pageYOffset || 0;
    return assertedY >= 0 && Math.abs(cur - assertedY) > 40;
  }
  function holdOffset(y) {
    if (takenOver() || scrollLocked()) return false;
    var cur = window.pageYOffset || 0;
    if (Math.abs(cur - y) > 1) window.scrollTo(0, y);
    assertedY = window.pageYOffset || 0;
    return true;
  }

  /* How far the real card sits from the ghost card drawn over it. null when
     there is nothing to compare. */
  function ghostGap(g, s) {
    var ghost = byClass('scg-ghost');
    var first = s.ghost && s.ghost[0];
    if (!ghost || !ghost.firstElementChild || !first || !first.href) return null;
    var a = g.querySelector('a[href="' + first.href.replace(/"/g, '\\"') + '"]');
    var li = a && a.closest('li');
    if (!li) return null;
    var card = li.firstElementChild || li;
    return card.getBoundingClientRect().top - ghost.firstElementChild.getBoundingClientRect().top;
  }

  function settleRestore(g, s, floor, refit, pagesDone) {
    var start = performance.now();
    var stopped = false;
    var frameId = null;
    function stop() {
      if (stopped) return;
      stopped = true;
      if (frameId !== null) cancelAnimationFrame(frameId);
      if (refit && refit.stop) refit.stop();
      ['touchstart', 'wheel', 'pointerdown', 'keydown'].forEach(function (name) {
        window.removeEventListener(name, takeOver);
      });
      if (stopRestoreLayout === stop) stopRestoreLayout = null;
    }
    function takeOver(event) {
      if (event.type === 'keydown' && !/^(ArrowUp|ArrowDown|PageUp|PageDown|Home|End| )$/.test(event.key)) return;
      stop();
      assertedY = -1;
      removeEl(byClass('scg-ghost'));
      root.style.overflowAnchor = '';
      root.removeAttribute('data-scg-header');
      /* Keep only the height reservation until missing pages arrive. Never
         move the viewport or the grid after the customer takes control. */
      try { history.scrollRestoration = 'auto'; } catch (e) {}
    }
    stopRestoreLayout = stop;
    ['touchstart', 'wheel', 'pointerdown', 'keydown'].forEach(function (name) {
      window.addEventListener(name, takeOver, { passive: true });
    });
    (function check() {
      if (stopped) return;
      if (grid() !== g) { stop(); return; }
      if (takenOver()) { takeOver({ type: 'scroll' }); return; }
      if (!pagesDone() && performance.now() - start < 4000) {
        /* Pages are still going in. Keep absorbing every shift from the very
           first frame — waiting until they finish let the header's transition
           move the grid ~29 px under the ghost between insertions. */
        if (refit) refit();
        frameId = requestAnimationFrame(check);
        return;
      }
      /* Every frame, not just when something resizes: the header's late
         compensation margin moves the grid without resizing anything. */
      if (refit) refit();
      var gap = ghostGap(g, s);
      var aligned = gap === null || Math.abs(gap) <= 1;
      var natural = Math.round(g.getBoundingClientRect().bottom + window.pageYOffset);
      if ((natural >= floor - 2 && aligned) || performance.now() - start > 4000 || grid() !== g) {
        holdOffset(s.y);
        root.style.overflowAnchor = '';
        if (pagesDone()) root.style.minHeight = '';
        /* The entry is ours no longer: hand scroll restoration back so an
           ordinary reload of this page behaves like any other. */
        try { history.scrollRestoration = 'auto'; } catch (e) {}
        /* The header read this during its own init, long before this
           deferred script ran; nothing must find it stale later. */
        root.removeAttribute('data-scg-header');
        liftGhost(g, s);
        stop();
        return;
      }
      frameId = requestAnimationFrame(check);
    })();
  }

  /* Returns true when it has taken ownership of arming the observer. */
  function restore() {
    var injected = root.getAttribute('data-scg-injected');
    if (!hasIO || !isReturn()) { clearRestoreArtifacts(); return false; }
    var s = saved();
    var g = grid();
    if (!s || !g || !g.children.length) { clearRestoreArtifacts(); return false; }
    restoringPages = true;

    /* The head script put back what it could (pages 2..k) before first paint
       and left a spacer for the rest. Without it — no session pages, or the
       script did not run — start from page 1; the ghost still covers. */
    var k = injected != null ? Math.max(1, parseInt(injected, 10) || 1) : 1;
    root.removeAttribute('data-scg-injected');
    root.removeAttribute('data-scg-restoring');
    lastInserted = k;

    /* The height floor. One `min-height` on the root, set to the full
       document height she left (grid top + grid height). The browser's own
       scroll restoration lands on her real offset against it, and because it
       is only ever *released* — never rebuilt — the document can never
       momentarily shrink under her while pages stream in. It survives Dawn's
       `div:empty` rule (it is not a child element) and anchoring (it is the
       scroll container). Released the instant the real grid has genuinely
       reached that height. Replaces the removable hold spacer, whose
       remove-then-refill was the one thing that made the offset collapse
       (owner, 2026-09-12). The pad above the grid handles the header gap. */
    var floor = Math.round(s.gt + s.h);
    root.style.minHeight = floor + 'px';
    var refit = guardFlow(g, s.gt);
    holdOffset(s.y);
    var pagesIn = false;
    settleRestore(g, s, floor, refit, function () { return pagesIn; });
    /* Her viewport can sit inside the floored space while pages go in below
       the fold; anchoring would otherwise follow the growing grid. */
    root.style.overflowAnchor = 'none';

    learnBase(sentinel());

    function finish() {
      if (grid() !== g) return;
      restoringPages = false;
      var b = sentinel();
      if (b) { learnBase(b); nextPage = lastInserted + 1; }
      else { nextPage = null; if (s.end) latched = true; }
      syncPagination();
      measureSentinel();
      arm();
      prefetch(nextPage);
      pagesIn = true;
      if (!stopRestoreLayout) root.style.minHeight = '';
    }

    function step(n) {
      if (grid() !== g) return;
      if (n > s.last) return finish();
      loadPage(n).then(function (page) {
        if (grid() !== g) return;
        appendChunked(g, nodesOf(page.lis), function () {
          lastInserted = n;
          var b = sentinel();
          if (b) placeSentinel(b, page.more);
          if (refit) refit();
          step(n + 1);
        });
      }).catch(function () { finish(); });
    }
    step(k + 1);
    return true;
  }

  /* ------------------------------------------------------------------ */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.classList || !input.classList.contains('mobile-facets__sort-input')) return;
    var val = document.querySelector('.mfbar__sort-val');
    if (!val) return;
    var label = input.closest('label');
    var text = label && label.querySelector('.mobile-facets__sort-text');
    if (!text) return;
    val.textContent = text.textContent.trim();
    var control = val.closest('.mfbar__sort');
    if (control) {
      var base = control.getAttribute('data-sort-label') || '';
      control.setAttribute('aria-label', base ? base + ': ' + val.textContent : val.textContent);
    }
  });

  /* facets.js swaps the container's children and fires no event; watch the
     container so a filtered result gets its pagination state and its own
     sentinel armed. The theme editor re-renders the whole section (a new
     container element), so `init` runs again on shopify:section:load and
     watches the new one — otherwise the editor preview would show the strip
     and stop auto-loading after any settings change. */
  var watched = null;
  var watchedGrid = null;
  var restoreTried = false;

  function fresh(deferMeasure) {
    restoringPages = false;
    if (stopRestoreLayout) stopRestoreLayout();
    latched = false;
    fallback = false;
    inserting = false;
    mem = {};
    dead = {};          /* a new result set is a new attempt — never inherit a back-off */
    lastInserted = 1;
    var box = sentinel();
    learnBase(box);
    nextPage = box ? 2 : null;
    syncPagination();
    if (deferMeasure) {
      // A replaced grid has just invalidated layout. Do not force it from
      // the mutation observer; the native intersection observer can arm now.
      // Ignore the old position until the new grid has painted and measured.
      sentinelTop = Infinity;
      var current = grid();
      requestAnimationFrame(function () {
        setTimeout(function () {
          if (grid() === current) measureSentinel();
        }, 0);
      });
    } else {
      measureSentinel();
    }
  }

  function init() {
    fresh();
    /* One restore attempt per document, and only before anything else has
       armed the observer — `restore` arms it itself when it takes over. */
    var owned = !restoreTried && restore();
    restoreTried = true;
    if (!owned) { arm(); prefetch(nextPage); }
    var c = container();
    watchedGrid = grid();
    if (c && c !== watched && 'MutationObserver' in window) {
      watched = c;
      /* facets.js replaces the container's innerHTML, which swaps the
         #product-grid element for a new one. That element-identity change is
         the only thing that means "new results" — NOT any childList mutation,
         because this script inserts its own spacers, sentinel and cards into
         the same container, and reacting to those would tear down the very
         restore/auto-load that produced them (owner, 2026-09-12: that
         self-trigger clamped the offset mid-restore and stalled the pointer).
         So compare the grid element and act only when it is genuinely new. */
      new MutationObserver(function () {
        var g = grid();
        if (!g || g === watchedGrid) return;
        watchedGrid = g;
        clearRestoreArtifacts();
        fresh(true);
        arm();
        prefetch(nextPage);
      }).observe(c, { childList: true, subtree: true });
    }
  }

  init();
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#ProductGridContainer')) init();
  });
})();
